"use client";

import { usePathname } from "next/navigation";
import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { useIsomorphicLayoutEffect } from "usehooks-ts";
import { createStore, useStore, type StoreApi } from "zustand";
import { CookieName } from "@utilities/cookieKeys";
import { getCookieValue, setSessionCookie } from "@utilities/cookies";

type HasNavigatedStore = StoreApi<{ hasNavigated: boolean }>;

const defaultStore = createStore(() => ({ hasNavigated: false }));
const HasNavigatedContext = createContext<HasNavigatedStore>(defaultStore);

/**
 * `true` once the visitor has moved between pages within the site this session.
 *
 * Drives the "minimal" navigation bar: a visitor who lands cold on a minimal
 * page (e.g. from organic search) sees only the logo + CTAs, but once they
 * start browsing the full nav appears.
 *
 * Returns reactive state, not a one-off cookie read, so that when the value
 * flips mid-session the navbar re-renders and reveals the full nav — even when
 * the minimal page is reached via client-side navigation, where no reload would
 * otherwise occur. Defaults to `false` outside a provider (treated as cold).
 */
export const useHasNavigated = () => {
  const store = useContext(HasNavigatedContext);
  return useStore(store, s => s.hasNavigated);
};

export const HasNavigatedProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const lastPathname = useRef<string | null>(null);
  const [store] = useState(() => createStore(() => ({ hasNavigated: false })));

  // A layout effect (runs before paint) keeps returning visitors from flashing
  // the cold state. It lives here rather than in analytics so it still works
  // when PostHog is blocked.
  useIsomorphicLayoutEffect(() => {
    if (!pathname) return;

    if (getCookieValue(CookieName.HasNavigated)) {
      // Cookie already set earlier this session (e.g. on refresh or a later
      // page) — this visitor is browsing, so reveal the full nav.
      store.setState({ hasNavigated: true });
    } else if (lastPathname.current !== null && lastPathname.current !== pathname) {
      // The pathname changed, so this is the session's first navigation. Record
      // it in a cookie (read pre-hydration by NavigationBar's inline script on
      // future loads) and reveal the full nav now.
      //
      // We compare pathnames instead of a "first render" boolean so that React
      // Strict Mode's double-invoked effect doesn't mistake the initial mount
      // for a navigation and set the cookie on the cold landing.
      setSessionCookie(CookieName.HasNavigated, "1");
      store.setState({ hasNavigated: true });
    }

    lastPathname.current = pathname;
  }, [pathname, store]);

  return <HasNavigatedContext.Provider value={store}>{children}</HasNavigatedContext.Provider>;
};
